import type { ChangeEvent, HTMLAttributes, LegacyRef } from "react"
import React, { Fragment, forwardRef, useCallback, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline"
import { Box, Grid } from "@mui/material"
import {
  Button,
  type InputChangeEvent,
  InputText,
  NotificationUtils,
  Select,
  Typography,
} from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { useFormikContext } from "formik"
import { v4 as uuidv4 } from "uuid"

import { FeaturesMenu } from "../../../components/form/FeaturesMenu"
import { RadioButtonsCondition } from "../../../components/form/RadioButtonsCondition"
import { UploadFileBlock } from "../../../components/form/UploadFileBlock"
import { useDebounce } from "../../../hooks/useDebounce"
import { KonanAPI } from "../../../services/KonanAPI"
import { queryClient } from "../../../store/QueryClientStore"
import type { RootState } from "../../../store/ReduxStore"
import type { UpdateWorkflowRequest } from "../../../types/custom/projects"
import { Operators } from "../../../types/custom/projects"
import type { NewFeatureData, Rule } from "../../../types/custom/rules"
import { MapValueTypeToFeatureType } from "../../../types/custom/rules"
import { DataFileGroup } from "../../../types/generated/api/DataFileGroup"
import type { DataFileGroupList } from "../../../types/generated/api/DataFileGroupList"
import { WorkflowInputFeature } from "../../../types/generated/api/WorkflowInputFeature"
import { type StandardErrorType, withErrorHandler } from "../../../utils/ErrorHandling"
import {
  getFieldErrorIfTouched,
  getRuleWeightValue,
  handleFeatureChange,
  multiValuesOperators,
  operatorsBasedOnFeatureType,
} from "../../../utils/conditionHelpers"
import { FILE_ID_MATCHER, isTypeIncluded, matchFile } from "../../../utils/rulesetHelpers"
import { DataFilesMenu } from "../../Ruleset/components/DataFilesMenu"
import { DataBlock } from "../../Ruleset/components/RuleCard"
import type { FeatureData, SchemaFeature } from "../../Workflows"
import { useWorkflowFeatures } from "../../Workflows/utils/workflowHelpers"
import type { ConditionPath, ScoreCardFormikValues, ScorecardRule } from "../Interfaces"
import {
  getCondition as getConditionByPath,
  getFieldPath,
  updateConditionValue as updateConditionValueByPath,
} from "../helpers"

import styles from "./Scorecard.module.scss"

interface AdornmentProps extends HTMLAttributes<HTMLSpanElement> {
  onClick: () => void
  anchorEl: HTMLElement | null
  fill?: string
}
export const ArrowAdornment = forwardRef(
  (props: AdornmentProps, ref: LegacyRef<HTMLSpanElement>): React.ReactElement => {
    const { onClick, anchorEl, fill } = props

    return (
      <span ref={ref} onClick={() => onClick()} style={{ pointerEvents: "none" }}>
        {!anchorEl ? (
          <svg
            fill={fill}
            className="adornment"
            width="12"
            height="8"
            viewBox="0 0 12 8"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.59 0.294922L6 4.87492L1.41 0.294922L0 1.70492L6 7.70492L12 1.70492L10.59 0.294922Z"
              className="adornment"
            />
          </svg>
        ) : (
          <svg
            fill={fill}
            className="adornment"
            width="12"
            height="8"
            viewBox="0 0 12 8"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 0.294922L0 6.29492L1.41 7.70492L6 3.12492L10.59 7.70492L12 6.29492L6 0.294922Z"
              className="adornment"
            />
          </svg>
        )}
      </span>
    )
  },
)

type Props = {
  isCreateMode: boolean
  isEditMode: boolean
  handleRemoveRule?: () => void
  id: string
  index: number
  isScorecardLoading?: boolean
  ruleIndex: number
  path: ConditionPath
  removeCondition?: () => void
}

/**
 * single condition/rule component
 * @param {boolean} isCreateMode indicator if scorecard is in isCreateMode
 * @param {boolean} isEditMode indicator if scorecard is in isEditMode
 * @param {function} handleRemoveRule handler function executes upon removing rule
 * @param {string} id id for condition so formik can track it
 * @param {number} index index for conditionArray
 * @param {boolean} isScorecardLoading indicator for scorecard loading/currently updating or not
 * @returns {React.ReactElement}
 */
export function Condition({
  isCreateMode,
  isEditMode,
  index,
  id,
  removeCondition,
  isScorecardLoading,
  ruleIndex,
  path,
}: Readonly<Props>): React.ReactElement {
  const { id: projectID } = useParams<{ id: string }>()
  // variable contains all features (schema, computed, constant)
  const features: FeatureData[] = useWorkflowFeatures(
    new Set(["SchemaFeatures", "ComputedFeatures", "ConstantFeatures"]),
  )

  const workflowsList = useSelector((state: RootState) => state?.workflow?.workflows)

  const { values, setFieldValue, handleChange, handleBlur, setValues, isSubmitting, errors, touched } =
    useFormikContext<ScoreCardFormikValues>()

  const { t } = useTranslation("scorecards")

  // filtered features for LHS feature input
  const [filteredFeatures, setFilteredFeatures] = useState<Array<FeatureData>>(features ?? [])
  // filtered features for RHS feature input
  const [secondFilteredFeatures, setSecondFilteredFeatures] = useState<Array<FeatureData>>(features ?? [])
  // anchor for LHS feature
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  // anchor for RHS feature
  const [anchorEl2, setAnchorEl2] = useState<null | HTMLElement>(null)
  const [filesMenuAnchor, setFilesMenuAnchor] = useState<null | HTMLElement>(null)
  const [selectedFeat, setSelectedFeat] = useState<string>("")

  // TODO: change later when we add helper function to detect initial operators based on existing feature type
  const [operators, setOperators] = useState<Operators[] | string[]>(operatorsBasedOnFeatureType.UNDEFINED)

  // ref for LHS feature
  const inputRef = useRef<HTMLInputElement>(null)
  // ref for RHS feature
  const inputRef2 = useRef<HTMLInputElement>(null)
  const featuresMenuRef = useRef<HTMLInputElement>(null)

  const abortControllerRef = useRef<AbortController | null>(null)

  const condition = getConditionByPath({ values, ruleIndex, path })

  // update a workflow
  const updateWorkflowMutation = useMutation<AxiosResponse, AxiosError, UpdateWorkflowRequest>(
    KonanAPI.updateWorkflow,
    {
      onSuccess: async (res) => {
        // success snackbar - feature added to schema
        NotificationUtils.toast(t("notifications.featureAddedToSchema"), {
          severity: "success",
        })

        // invalidate queries
        await queryClient.invalidateQueries("workflows")
        await queryClient.invalidateQueries("workflow")
        await queryClient.invalidateQueries(["project", projectID as string])

        setFilteredFeatures(features.filter((item: FeatureData) => item?.name.includes(condition?.feature ?? "")))

        setSecondFilteredFeatures(
          features.filter((item: FeatureData) => item?.name.includes(condition?.secondFeature ?? "")),
        )

        const currentFeat = res?.data?.schema?.features?.find(
          (feat: SchemaFeature) => feat?.name === condition?.feature,
        )

        if (currentFeat && currentFeat?.type === WorkflowInputFeature.type.BOOLEAN) {
          setFieldValue(getFieldPath({ field: "type", ruleIndex, path }), "true")
          setTypes(["true", "false"])
        }
      },

      onError: () => {
        NotificationUtils.toast(t("notifications.featureAddToSchemaError"), {
          severity: "error",
        })
      },
    },
  )

  const handleAddDataFile = (file: DataFileGroupList): void => {
    setValues((prev) => {
      if (!prev.cards[ruleIndex]) return prev

      prev.cards[ruleIndex].condition_list_files = [
        ...prev.cards[ruleIndex].condition_list_files,
        { uuid: file.uuid, name: file.name, version: "" },
      ]

      const cond = getConditionByPath({ values: prev, ruleIndex, path })
      if (cond) cond.value = `$file<${file.uuid}>`
      return { ...prev }
    })
  }

  const {
    isLoading: isCsvUploading,
    mutateAsync: uploadCSVAsyncMutation,
    reset,
  } = useMutation<DataFileGroup, AxiosError<StandardErrorType>, File>(
    (file: File) => {
      abortControllerRef.current = new AbortController()
      return KonanAPI.uploadDataFile({
        project_uuid: projectID as string,
        file: file,
        signal: abortControllerRef.current.signal,
        type: DataFileGroup.type.CONDITION_LIST,
      })
    },
    {
      mutationKey: "uploadCSV",
      onSuccess: async (response) => handleAddDataFile(response),
      onError: withErrorHandler(() => {
        if (condition) condition.value = ""
      }),
    },
  )

  // handle upload once files are accepted
  const handleUpload = async (file: File): Promise<void> => {
    try {
      await uploadCSVAsyncMutation(file)

      // Invalidate training data to trigger a refresh
      await queryClient.invalidateQueries(["data-files", projectID])
    } catch {
      // reset dropzone on error
      handleCloseAndAbort()
    }
  }

  // handle close dropzone
  const handleCloseAndAbort = useCallback(() => {
    abortControllerRef.current?.abort()
    reset()
  }, [reset])

  // handle delete data file
  async function handleDeleteFile(): Promise<void> {
    setValues((prev) => {
      const cond = getConditionByPath({ values: prev, ruleIndex, path })
      const conditionValue = cond?.value

      if (!prev.cards[ruleIndex]) return { ...prev }

      prev.cards[ruleIndex].condition_list_files = (prev.cards?.[ruleIndex]?.condition_list_files || []).filter(
        (file: { uuid: string; name: string }) => file.uuid !== conditionValue?.match(FILE_ID_MATCHER)?.[1],
      )

      return updateConditionValueByPath({ values: prev, ruleIndex, path, newValue: "" })
    })
  }

  // special handler for changing type field and all side effects in specific cases
  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFieldValue(getFieldPath({ field: "type", ruleIndex, path }), event.target.value)

    //When selecting true, false, or null from the menu -> set the value field to the selection
    if (isTypeIncluded(event?.target?.value)) {
      setFieldValue(getFieldPath({ field: "value", ruleIndex, path }), event.target.value)
      setOperators([Operators["!="], Operators["="]])
    }

    // whenever number type is selected, change the operators to include all numerical operators
    if (event.target.value === "number") {
      setOperators(Object.values(Operators).filter((v) => isNaN(Number(v))))
    }

    if (event.target.value === "string") {
      setOperators(operatorsBasedOnFeatureType.TEXT)
    }

    // When selecting number or string from the menu -> reset the value field
    else if (
      ["string", "number"].includes(event.target.value) &&
      !["string", "number"].includes(condition?.type as string)
    ) {
      if (matchFile(condition?.value as string).isMatching) {
        handleDeleteFile()
      }
      setFieldValue(getFieldPath({ field: "value", ruleIndex, path }), "")
    }
  }

  // special handler for changing operator field and all side effects in specific cases
  const handleOperatorChange = (event: InputChangeEvent<Element>): void => {
    if (["is null", "not null"].includes(event.target.value.toString())) {
      setFieldValue(getFieldPath({ field: "operator", ruleIndex, path }), event.target.value)

      setFieldValue(getFieldPath({ field: "value", ruleIndex, path }), "null")
      setFieldValue(getFieldPath({ field: "type", ruleIndex, path }), "null")
      return
    }

    if (
      !["is null", "not null"].includes(event.target.value.toString()) &&
      ["is null", "not null"].includes(condition?.operator as string)
    ) {
      setFieldValue(getFieldPath({ field: "value", ruleIndex, path }), "")
      setFieldValue(getFieldPath({ field: "type", ruleIndex, path }), "string")
    }

    setFieldValue(getFieldPath({ field: "operator", ruleIndex, path }), event.target.value)

    // reset the value field when selecting in/not in operators from the menu
    if (
      multiValuesOperators.includes(Operators[event.target.value as keyof typeof Operators]) &&
      !multiValuesOperators.includes(Operators[condition?.operator as keyof typeof Operators])
    ) {
      if (matchFile(condition?.value as string).isMatching) {
        handleDeleteFile()
      }
      setFieldValue(getFieldPath({ field: "value", ruleIndex, path }), "")
    }

    if (
      [Operators.between, ...multiValuesOperators].includes(Operators[event.target.value as keyof typeof Operators])
    ) {
      setFieldValue(getFieldPath({ field: "valueOrFeature", ruleIndex, path }), "Value")
    }

    if (
      matchFile(condition?.value as string).isMatching &&
      !multiValuesOperators.includes(Operators[event.target.value as keyof typeof Operators]) &&
      multiValuesOperators.includes(Operators[condition?.operator as keyof typeof Operators])
    ) {
      handleDeleteFile()
    }

    // automatically switch value's type to number when these operators are active
    // -> "greater than", "greater than/equal", "lower than", "lower than/equal", "between"
    if (
      ["greater than", "greater than/equal", "lower than", "lower than/equal", "between"].includes(
        event.target.value.toString(),
      )
    ) {
      setFieldValue(getFieldPath({ field: "type", ruleIndex, path }), "number")
    }

    // automatically switch value's type to string when these operators are active
    // -> "in", "not in", "contains", "not contains"
    if (multiValuesOperators.includes(event.target.value.toString())) {
      setFieldValue(getFieldPath({ field: "type", ruleIndex, path }), "string")
    }
  }

  const operatorSizeCheck = (): boolean =>
    // check if the operator is one of ["equal", "not equal"]
    ["equal", "not equal"].includes(condition?.operator as string) &&
    condition?.valueOrFeature === "Value" &&
    (isEditMode || isCreateMode) &&
    (!currentFeature ||
      currentFeature?.type === WorkflowInputFeature.type.UNDEFINED ||
      currentFeature?.type === WorkflowInputFeature.type.BOOLEAN)

  const [types, setTypes] = useState<Array<string>>(["string", "number", "true", "false"])

  // return value field type based on conditions
  const getValueFieldType = (): string => {
    return multiValuesOperators.includes(condition?.operator as string)
      ? "text"
      : condition?.type === "number"
        ? "number"
        : "text"
  }

  const currentFeature = useMemo(() => {
    const feature = condition?.feature
    if (features?.length > 0 && feature) {
      return features?.find((feat) => feat.name === feature)
    }
  }, [features, condition?.feature])

  const handleUpdateWorkflow = async (featureData: NewFeatureData, featureName: string): Promise<void> => {
    // Fetch single workflow
    const { data: workflowData } = await queryClient.fetchQuery(["workflow", projectID, featureData.workflow], () =>
      KonanAPI.fetchWorkflow(projectID as string, featureData.workflow),
    )

    const newFeat = {
      name: featureName?.replace(/\s/g, ""),
      type: featureData.type,
      is_required: featureData.isRequired,
      id: uuidv4(),
      source: "workflow",
      new: true,
    }

    if (workflowData?.active_version) {
      await updateWorkflowMutation.mutateAsync({
        name: workflowData?.name,
        projectId: projectID as string,
        workflowUUID: workflowData?.uuid,
        schema: [...(workflowData?.active_version?.schema?.features ?? []), newFeat],
      })
    }
  }

  const debouncedOnChangeLHS = useDebounce((): void => {
    const updatedConditionParts = handleFeatureChange(features, condition?.feature as string, condition as Rule)
    setFilteredFeatures(updatedConditionParts?.filteredFeatures as FeatureData[])
    setSecondFilteredFeatures(updatedConditionParts?.otherFilteredFeatures as FeatureData[])
    setOperators(updatedConditionParts?.operators)
    setFieldValue(getFieldPath({ field: "operator", ruleIndex, path }), updatedConditionParts?.operator)

    setTypes(updatedConditionParts?.types)

    setFieldValue(getFieldPath({ field: "type", ruleIndex, path }), updatedConditionParts?.type)

    setFieldValue(getFieldPath({ field: "value", ruleIndex, path }), updatedConditionParts?.value)

    setAnchorEl(inputRef?.current)
    setTimeout(() => {
      inputRef?.current?.focus()
    }, 100)
  }, 400)

  const debouncedOnChangeRHS = useDebounce((): void => {
    const updatedConditionParts = handleFeatureChange(features, condition?.secondFeature as string, condition as Rule)
    setFilteredFeatures(updatedConditionParts?.otherFilteredFeatures as FeatureData[])
    setSecondFilteredFeatures(updatedConditionParts?.filteredFeatures as FeatureData[])
    setOperators(updatedConditionParts?.operators)
    setFieldValue(getFieldPath({ field: "operator", ruleIndex, path }), updatedConditionParts?.operator)

    setTypes(updatedConditionParts?.types)

    setFieldValue(getFieldPath({ field: "type", ruleIndex, path }), updatedConditionParts?.type)

    setFieldValue(getFieldPath({ field: "value", ruleIndex, path }), updatedConditionParts?.value)

    setAnchorEl2(inputRef2?.current)
    setTimeout(() => {
      inputRef2?.current?.focus()
    }, 100)
  }, 400)

  const handleLHSFeatureChange = (event: InputChangeEvent): void => {
    handleChange(event)
    setAnchorEl(null)
    debouncedOnChangeLHS()
  }

  const handleFeatureSelectionFromMenu = (item: FeatureData, featureIndicator: "LHS" | "RHS"): void => {
    setSelectedFeat(item?.name)
    const updatedConditionParts = handleFeatureChange(features, item?.name, condition as Rule)

    setFilteredFeatures(updatedConditionParts?.filteredFeatures as FeatureData[])
    setSecondFilteredFeatures(updatedConditionParts?.otherFilteredFeatures as FeatureData[])
    setOperators(updatedConditionParts?.operators)
    setFieldValue(getFieldPath({ field: "operator", ruleIndex, path }), updatedConditionParts?.operator)

    setTypes(updatedConditionParts?.types)

    setFieldValue(getFieldPath({ field: "type", ruleIndex, path }), updatedConditionParts?.type)

    setFieldValue(getFieldPath({ field: "value", ruleIndex, path }), updatedConditionParts?.value)

    if (featureIndicator === "LHS") {
      setFilteredFeatures(updatedConditionParts?.filteredFeatures as FeatureData[])
      setSecondFilteredFeatures(updatedConditionParts?.otherFilteredFeatures as FeatureData[])
    } else {
      setFilteredFeatures(updatedConditionParts?.otherFilteredFeatures as FeatureData[])
      setSecondFilteredFeatures(updatedConditionParts?.filteredFeatures as FeatureData[])
    }
  }

  /**
   * indicator flag for displaying value/feature options (radio buttons options)
   */
  const shouldDisplayValueOrFeature =
    // 1. when scorecard in edit/create mode
    (isEditMode || isCreateMode) &&
    // 2. if operator is not in [in, not in, contains, not contains]
    !["is null", "not null", "between"].includes(condition?.operator as string) &&
    // 3. if value type isn't in [true, false, null] OR equal to any of these values
    // but feature is empty or currentFeature not equal to boolean or undefined
    (!isTypeIncluded(condition?.type as string) ||
      (isTypeIncluded(condition?.type as string) &&
        currentFeature &&
        currentFeature?.type !== WorkflowInputFeature.type.BOOLEAN &&
        currentFeature?.type !== WorkflowInputFeature.type.UNDEFINED))

  /**
   * indicator flag for displaying type field
   */
  const shouldDisplayTypeField =
    // 1. when scorecard in edit/create mode
    (isEditMode || isCreateMode) &&
    // 2. when i'm not comparing between another feature
    condition?.valueOrFeature !== "Feature" &&
    // 3. when operator value is equal or not equal
    [Operators["!="], Operators["="]].includes(condition?.operator as Operators) &&
    !["is null", "not null"].includes(condition?.operator as string) &&
    // 4. if feature is empty or currentFeature undefined or the type of the currentFeature is undefined
    (condition?.feature === "" ||
      !currentFeature ||
      (currentFeature &&
        [WorkflowInputFeature.type.UNDEFINED, WorkflowInputFeature.type.BOOLEAN].includes(
          WorkflowInputFeature.type[currentFeature?.type as keyof typeof WorkflowInputFeature.type],
        )))

  /**
   * indicator flag for hiding value field
   */
  const shouldHideValueField =
    // 1. when scorecard in edit/create mode
    ((isEditMode || isCreateMode) &&
      // 2. if value type is in [true, false, null] or equal to any of these values
      isTypeIncluded(condition?.type as string) &&
      // 3. if operator isn't equal to [contains, not contains, in, not in]
      !multiValuesOperators.includes(condition?.operator as string) &&
      // 4. if current feature type is either undefined or boolean and type is in [true, false, null]
      // or current feature not defined
      ((currentFeature &&
        [WorkflowInputFeature.type.UNDEFINED, WorkflowInputFeature.type.BOOLEAN].includes(
          WorkflowInputFeature.type[currentFeature?.type as keyof typeof WorkflowInputFeature.type],
        )) ||
        condition?.feature === "" ||
        !currentFeature)) ||
    ["is null", "not null"].includes(condition?.operator as string)

  /**
   * indicator for displaying the file block that contains the file name used in condition
   */
  const shouldDisplayFileBlock = Boolean(
    (matchFile(condition?.value as string).isMatching || isCsvUploading) &&
      multiValuesOperators.includes(condition?.operator as string) &&
      (isEditMode || isCreateMode),
  )

  const getInitialNewFeatureType = (): WorkflowInputFeature.type => {
    if ([Operators.between, ...multiValuesOperators].includes(condition?.operator as string)) {
      return WorkflowInputFeature.type.LIST
    } else if (condition?.type === "true" || condition?.type === "false") {
      return WorkflowInputFeature.type.BOOLEAN
    } else if (condition?.type === "number") {
      return WorkflowInputFeature.type.NUMBER
    } else {
      return WorkflowInputFeature.type.TEXT
    }
  }

  const handleFeatureTypeChange = (value: string): void => {
    switch (value) {
      case WorkflowInputFeature.type.TEXT:
      case WorkflowInputFeature.type.UNDEFINED:
      case WorkflowInputFeature.type.DATE:
        if (
          (condition?.type === "true" && condition?.value === "true") ||
          (condition?.type === "false" && condition?.value === "false")
        ) {
          setFieldValue(id + ".value", "")
        }

        setFieldValue(id + ".type", "string")
        setOperators(operatorsBasedOnFeatureType.TEXT)
        setFieldValue(id + ".operator", "equal")
        break

      case WorkflowInputFeature.type.BOOLEAN:
        setFieldValue(id + ".type", "true")
        setFieldValue(id + ".value", "true")
        setOperators([Operators["!="], Operators["="]])
        setFieldValue(id + ".operator", "equal")
        break

      case WorkflowInputFeature.type.NUMBER:
        if (
          (condition?.type === "true" && condition?.value === "true") ||
          (condition?.type === "false" && condition?.value === "false")
        ) {
          setFieldValue(id + ".value", "")
        }

        setFieldValue(id + ".type", "number")
        setOperators(Object.values(Operators).filter((v) => isNaN(Number(v))))
        setFieldValue(id + ".operator", "equal")
        break

      case WorkflowInputFeature.type.LIST:
        setFieldValue(id + ".operator", "in")

        // reset the value field when selecting in/not in operators from the menu
        setFieldValue(id + ".value", "")
        setFieldValue(id + ".valueOrFeature", "Value")
        break
      default:
        break
    }
  }

  // function that accepts an operator translation and return its value from `operators` enum
  const getOpValuefromTranslation = (translation: string): string | undefined => {
    for (const value of Object.values(operators)) {
      if (t(`operators.${value}`) === translation) {
        return value
      }
    }
  }

  const valueInputDescription = useMemo(() => {
    return [Operators["in"], Operators["not in"]].includes(Operators[condition?.operator as keyof typeof Operators])
      ? t("valueHelp.inNotIn")
      : [Operators["contains"], Operators["not contains"]].includes(
            Operators[condition?.operator as keyof typeof Operators],
          )
        ? t("valueHelp.containsNotContains")
        : ""
  }, [condition, t])

  return (
    <Grid container item className={styles.conditionContainer} spacing={1}>
      <Grid item container alignItems="center" gap={1} xs={values.cards[ruleIndex]?.type === "simple" ? 10 : 12}>
        {/* And/Or */}
        {index !== 0 ? (
          <Grid item lg={1.5}>
            {isEditMode || isCreateMode ? (
              <Select
                id={id + ".andOr"}
                type="text"
                value={condition?.andOr}
                handleBlur={handleBlur}
                handleChange={(e) => {
                  handleChange(e)
                  setFieldValue(getFieldPath({ field: "andOr", ruleIndex, path }), e.target.value)
                }}
                hideDescription
                menuProps={{ menuMaxContent: true }}
                fullWidth
                disabled={isSubmitting}
                options={["and", "or"]}
              />
            ) : (
              <DataBlock value={getConditionByPath({ values, ruleIndex, path })?.andOr} />
            )}
          </Grid>
        ) : (
          index === 0 &&
          values?.cards[ruleIndex]?.type === "complex" && (
            /* If */
            <Grid item lg={1.5} mt={1.5}>
              <Box display="flex" justifyContent="flex-end" alignItems="flex-end">
                <Typography variant="p">{t("if")}</Typography>
              </Box>
            </Grid>
          )
        )}

        {/* Feature */}
        <Grid item xs={isEditMode || isCreateMode ? 5.5 : 5} md={isEditMode || isCreateMode ? 3.1 : 2.9}>
          {isEditMode || isCreateMode ? (
            <div>
              <InputText
                hideDescription
                anchoringRef={inputRef}
                id={id + ".feature"}
                placeholder={t("inputFeaturePlaceholder")}
                endAdornment={
                  features?.length > 0 ? (
                    <ArrowAdornment
                      anchorEl={anchorEl}
                      onClick={() => setAnchorEl(!!anchorEl ? null : inputRef?.current)}
                    />
                  ) : undefined
                }
                handleChange={handleLHSFeatureChange}
                handleBlur={handleBlur}
                value={condition?.feature}
                error={getFieldErrorIfTouched(errors, touched, getFieldPath({ field: "feature", ruleIndex, path }))}
                disabled={isSubmitting || isScorecardLoading}
                fullWidth
              />
              {/* TODO:: check if we need to remove debouncer after revamp */}
              {/* as far as i remember it was made to accommodate MUI, so maybe we don't need it -> 9/2/2023 */}
              <FeaturesMenu
                filteredFeatures={filteredFeatures}
                key={`${filteredFeatures?.length}`}
                workflowsList={workflowsList}
                anchorEl={anchorEl}
                queryKey={condition?.feature as string}
                handleFeatureTypeChange={(value: string) => handleFeatureTypeChange(value)}
                onAddingNewFeature={(featureData) => {
                  setAnchorEl(null)
                  handleUpdateWorkflow(featureData, condition?.feature as string)
                }}
                onSelectingFeature={(item) => {
                  setFieldValue(id + ".feature", item?.name)
                  handleFeatureSelectionFromMenu(item, "LHS")
                  setAnchorEl(null)
                }}
                onMenuClose={() => setAnchorEl(null)}
                initialFeatureType={getInitialNewFeatureType}
              />
            </div>
          ) : (
            <DataBlock value={condition?.feature} />
          )}
        </Grid>

        {/* Operator */}
        <Grid
          item
          lg={operatorSizeCheck() ? 1.9 : true}
          md={operatorSizeCheck() ? 2 : true}
          xs={operatorSizeCheck() ? 3 : true}
        >
          {isEditMode || isCreateMode ? (
            <Select
              id={id + ".operator"}
              disabled={isScorecardLoading}
              type="text"
              handleBlur={handleBlur}
              value={t(`operators.${condition?.operator}`)}
              handleChange={(e) => {
                e.target.value = getOpValuefromTranslation(e.target.value as string) as string
                handleOperatorChange(e as ChangeEvent<HTMLInputElement>)
              }}
              fullWidth
              menuProps={{ menuMaxContent: true }}
              options={operators.map((op) => t(`operators.${op}`))}
              hideDescription
            />
          ) : (
            <DataBlock value={t(`operators.${condition?.operator}`)} />
          )}
        </Grid>

        {/* Type */}
        {shouldDisplayTypeField && (
          <Grid
            item
            xs={isEditMode || isCreateMode ? 3.5 : "auto"}
            md={isTypeIncluded(condition?.type as string) ? true : 1.9}
          >
            {isEditMode || isCreateMode ? (
              <Select
                id={id + ".type"}
                menuProps={{ menuMaxContent: true }}
                fullWidth
                disabled={isScorecardLoading}
                key={condition?.id as string}
                type="text"
                handleBlur={handleBlur}
                value={condition?.type}
                handleChange={(e) => handleTypeChange(e as React.ChangeEvent<HTMLInputElement>)}
                options={types}
                hideDescription
              />
            ) : (
              <DataBlock value={condition?.type} />
            )}
          </Grid>
        )}

        {/* Feature/value radio buttons*/}
        {shouldDisplayValueOrFeature && (
          <RadioButtonsCondition
            handleOnChange={(e) => {
              setFieldValue(id + ".valueOrFeature", e.target.value)

              setOperators(
                operatorsBasedOnFeatureType[
                  MapValueTypeToFeatureType[condition?.type as keyof typeof MapValueTypeToFeatureType]
                ],
              )
            }}
            checkedFeat={condition?.valueOrFeature === "Feature"}
            checkedValue={condition?.valueOrFeature === "Value"}
            shouldDisableFeature={matchFile(condition?.value as string).isMatching}
            isDisabled={isSubmitting}
          />
        )}

        {/* Double values*/}
        {condition?.operator === "between" ? (
          <Grid container item xs>
            <Grid gap={0.5} item display="flex" justifyContent="space-between" width="100%">
              <Grid item xs>
                {isCreateMode || isEditMode ? (
                  <InputText
                    hideDescription
                    id={id + ".value"}
                    key={condition?.id as string}
                    placeholder="Value"
                    value={condition?.value}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={getFieldErrorIfTouched(
                      errors,
                      touched,
                      getFieldPath({ field: "value", ruleIndex, path }).replaceAll("[", ".").replaceAll("]", ""),
                    )}
                    disabled={isSubmitting || isScorecardLoading}
                    fullWidth
                    type="number"
                  />
                ) : (
                  <DataBlock value={condition?.value} />
                )}
              </Grid>

              {/* And */}
              <Grid item width={"fit-content"} mt={isCreateMode || isEditMode ? 0.5 : 0} className="and-block">
                <Typography variant="h3-bold" color="base">
                  {t("and")}
                </Typography>
              </Grid>

              <Grid item xs>
                {isCreateMode || isEditMode ? (
                  <InputText
                    hideDescription
                    id={id + ".secondValue"}
                    key={condition?.id as string}
                    placeholder={t("inputValuePlaceholder")}
                    value={condition?.secondValue}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={getFieldErrorIfTouched(
                      errors,
                      touched,
                      getFieldPath({ field: "secondValue", ruleIndex, path }).replaceAll("[", ".").replaceAll("]", ""),
                    )}
                    disabled={isSubmitting || isScorecardLoading}
                    fullWidth
                    type="number"
                  />
                ) : (
                  <DataBlock value={condition?.secondValue} />
                )}
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Grid
            item
            xs
            //md={isEditMode ? (multiValuesOperators.includes(condition?.operator) ? 3 : 2) : 3.5}
            // Important: hide this field using display: "none" in order NOT to lose access to this element by id,
            // as the id of this field is used by the formik object
            key={condition?.id}
            style={shouldHideValueField ? { display: "none" } : undefined}
          >
            {shouldDisplayFileBlock ? (
              <UploadFileBlock
                onDelete={handleDeleteFile}
                fileName={
                  values?.cards?.[ruleIndex]?.condition_list_files?.find(
                    (file: { uuid: string; name: string }) =>
                      file.uuid === matchFile(condition?.value as string).fileId,
                  )?.name ?? ""
                }
                isUploading={isCsvUploading}
                handleCancel={handleCloseAndAbort}
                isReadMode={!isEditMode && !isCreateMode}
                isDisabled={isSubmitting}
                fileID={matchFile(condition?.value as string).fileId}
              />
            ) : (isEditMode || isCreateMode) && condition?.valueOrFeature === "Feature" ? (
              <div>
                <InputText
                  hideDescription
                  id={id + ".secondFeature"}
                  key={`${selectedFeat}_${values?.uuid}`}
                  placeholder={t("inputFeaturePlaceholder")}
                  anchoringRef={inputRef2}
                  value={condition?.secondFeature}
                  endAdornment={
                    features?.length > 0 ? (
                      <ArrowAdornment
                        anchorEl={anchorEl2}
                        onClick={() => setAnchorEl2(!!anchorEl2 ? null : inputRef2?.current)}
                      />
                    ) : undefined
                  }
                  handleChange={(e) => {
                    handleChange(e)
                    setAnchorEl2(null)
                    debouncedOnChangeRHS()
                  }}
                  handleBlur={handleBlur}
                  error={getFieldErrorIfTouched(
                    errors,
                    touched,
                    getFieldPath({ field: "secondFeature", ruleIndex, path }).replaceAll("[", ".").replaceAll("]", ""),
                  )}
                  disabled={isSubmitting || isScorecardLoading}
                  fullWidth
                />
                <FeaturesMenu
                  filteredFeatures={secondFilteredFeatures}
                  workflowsList={workflowsList}
                  anchorEl={anchorEl2}
                  queryKey={condition?.secondFeature as string}
                  onAddingNewFeature={(featureData) => {
                    setAnchorEl2(null)
                    handleUpdateWorkflow(featureData, condition?.secondFeature as string)
                  }}
                  onSelectingFeature={(item) => {
                    setFieldValue(id + ".secondFeature", item?.name)
                    handleFeatureSelectionFromMenu(item, "RHS")
                    setAnchorEl2(null)
                  }}
                  onMenuClose={() => setAnchorEl2(null)}
                />
              </div>
            ) : isEditMode || isCreateMode ? (
              <Fragment>
                {/* Value
                // TODO:: Refactor and create a modular component later */}
                <InputText
                  hideDescription
                  id={id + ".value"}
                  placeholder={t("inputValuePlaceholder")}
                  value={condition?.value}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  anchoringRef={featuresMenuRef}
                  endAdornment={
                    !isCsvUploading &&
                    multiValuesOperators.includes(Operators[condition?.operator as keyof typeof Operators]) ? (
                      <ArrowAdornment
                        anchorEl={filesMenuAnchor}
                        onClick={() => setFilesMenuAnchor(!!filesMenuAnchor ? null : featuresMenuRef?.current)}
                        style={{ color: "var(--base-text-2)" }}
                      />
                    ) : undefined
                  }
                  error={getFieldErrorIfTouched(
                    errors,
                    touched,
                    getFieldPath({ field: "value", ruleIndex, path }).replaceAll("[", ".").replaceAll("]", ""),
                  )}
                  disabled={isSubmitting || isScorecardLoading}
                  fullWidth
                  type={getValueFieldType()}
                  description={valueInputDescription}
                />

                {filesMenuAnchor && (
                  <DataFilesMenu
                    onDataFileUpload={handleUpload}
                    onAddDataFile={handleAddDataFile}
                    anchorEl={filesMenuAnchor}
                    onClose={() => setFilesMenuAnchor(null)}
                  />
                )}
              </Fragment>
            ) : (
              <DataBlock
                value={
                  matchFile(condition?.value as string).isMatching
                    ? (values?.cards[ruleIndex]?.condition_list_files?.find(
                        (file: { uuid: string; name: string }) =>
                          file.uuid === matchFile(condition?.value as string).fileId,
                      )?.name ?? "")
                    : condition?.value
                }
                fileId={
                  matchFile(condition?.value as string).isMatching
                    ? matchFile(condition?.value as string).fileId
                    : undefined
                }
              />
            )}
          </Grid>
        )}

        {/* Remove button */}
        {(isEditMode || isCreateMode) &&
          !(index === 0 && (path.groupsIdx?.length ?? 0) === 0) &&
          values?.cards[ruleIndex]?.type === "complex" && (
            <Grid display="flex" item alignSelf="center">
              <Button
                variant="dangerous"
                size="small"
                onClick={() => {
                  if (
                    multiValuesOperators.includes(Operators[condition?.operator as keyof typeof Operators]) &&
                    matchFile(condition?.value as string).isMatching
                  ) {
                    handleDeleteFile()
                  }
                  removeCondition?.()
                }}
                disabled={isSubmitting}
              >
                <RemoveCircleOutlineIcon fontSize="inherit" />
              </Button>
            </Grid>
          )}
      </Grid>

      {values.cards[ruleIndex]?.type === "simple" && (
        <Grid item flexGrow={1} container display="flex" xs alignSelf="flex-end">
          {/* = */}
          <Grid alignSelf="flex-end" item xs={"auto"} marginRight={1} marginTop={isEditMode || isCreateMode ? 2.5 : 1}>
            <Typography variant="a">{"="}</Typography>
          </Grid>
          {/* Rule weight */}
          <Grid key={condition?.id} item xs display={isEditMode || isCreateMode ? "flex" : "block"} flexGrow={1}>
            {isEditMode || isCreateMode ? (
              <InputText
                hideDescription
                id={`cards[${ruleIndex}].return_value`}
                key={values?.cards[ruleIndex]?.id}
                placeholder={t("inputRuleWeightPlaceholder")}
                value={getRuleWeightValue(values?.cards[ruleIndex]?.return_value)}
                handleChange={handleChange}
                handleBlur={handleBlur}
                error={getFieldErrorIfTouched(errors, touched, `cards.${ruleIndex}.return_value`)}
                disabled={isSubmitting || isScorecardLoading}
                type="number"
                fullWidth
              />
            ) : (
              <DataBlock value={`${parseFloat(values?.cards[ruleIndex]?.return_value)}`} />
            )}
          </Grid>
        </Grid>
      )}

      {/* Remove button */}
      {(isEditMode || isCreateMode) &&
        values?.cards[ruleIndex]?.type === "simple" &&
        ((values.cards[ruleIndex]?.conditions?.length === 1 && values.cards?.length > 1) ||
          index !== 0 ||
          (path.groupsIdx?.length ?? 0) > 0) && (
          <Grid display="flex" item alignSelf="center">
            <Button
              variant="dangerous"
              size="small"
              onClick={() => {
                const newRules = values?.cards?.filter((_: ScorecardRule, idx: number) => idx !== ruleIndex)

                setFieldValue(`cards`, newRules)
              }}
              disabled={isSubmitting}
            >
              <RemoveCircleOutlineIcon fontSize="inherit" />
            </Button>
          </Grid>
        )}
    </Grid>
  )
}
